import { __rest } from "tslib";
import * as React from 'react';
import { useEffect, useState, useMemo, useCallback, useRef, isValidElement, cloneElement } from 'react';
import { isImageValid, isAbsoluteUrl } from '../utils';
import { EventCategory, Log } from '../sentry';
import { useConfig } from '../context';
import { getCdnUrl, getContentUrl, isSvgUrl } from './utils';
import { getSvgContent } from '@shared/lib/svgCache';
import { SafeHtml } from '@shared/ui/SafeHtml';
const CdnImage = (_a) => {
    var { path, fallbackUrl, isBackground, contentRepo, children, alt = '', emptyImage, notifyOnError = false } = _a, imgProps = __rest(_a, ["path", "fallbackUrl", "isBackground", "contentRepo", "children", "alt", "emptyImage", "notifyOnError"]);
    const [primaryFailed, setPrimaryFailed] = useState(false);
    const [fallbackFailed, setFallbackFailed] = useState(false);
    const primaryFailedRef = useRef(primaryFailed);
    useEffect(() => {
        primaryFailedRef.current = primaryFailed;
    }, [primaryFailed]);
    const { global: { companyInfo: { iataCode } } } = useConfig();
    const reportError = useCallback((message) => {
        if (notifyOnError) {
            Log({
                category: EventCategory.CDN,
                level: 'warning',
                message
            });
        }
        else {
            console.error(message);
        }
    }, [notifyOnError]);
    // Авто-фолбэк для contentRepo: при ошибке подставляем ZZ вместо iataCode
    const contentFallbackUrl = contentRepo && !fallbackUrl && path && !isAbsoluteUrl(path) ? getContentUrl('ZZ', path) : undefined;
    const effectiveFallbackUrl = useMemo(() => {
        if (!contentRepo) {
            return fallbackUrl;
        }
        if (fallbackUrl) {
            if (isAbsoluteUrl(fallbackUrl)) {
                reportError(`CdnImage: contentRepo требует относительный fallbackUrl, получен: ${fallbackUrl}`);
                return contentFallbackUrl;
            }
            return getContentUrl('ZZ', fallbackUrl);
        }
        return contentFallbackUrl;
    }, [contentRepo, fallbackUrl, contentFallbackUrl, reportError]);
    useEffect(() => {
        setPrimaryFailed(false);
        setFallbackFailed(false);
    }, [path, effectiveFallbackUrl]);
    /**
     * Строим итоговый url для сырого пути:
     * - если путь уже абсолютный — используем как есть
     * - иначе строим через CDN или content-репозиторий
     */
    const buildUrl = useCallback((rawPath) => {
        if (!rawPath) {
            return '';
        }
        if (isAbsoluteUrl(rawPath)) {
            return rawPath;
        }
        return contentRepo ? getContentUrl(iataCode, rawPath) : getCdnUrl(iataCode, rawPath);
    }, [contentRepo, iataCode]);
    const primaryUrl = useMemo(() => {
        if (contentRepo && path && isAbsoluteUrl(path)) {
            reportError(`CdnImage: contentRepo требует относительный путь, получен: ${path}`);
            return '';
        }
        return buildUrl(path);
    }, [buildUrl, contentRepo, path, reportError]);
    const resolvedFallbackUrl = useMemo(() => buildUrl(effectiveFallbackUrl), [buildUrl, effectiveFallbackUrl]);
    // Фолбэк, совпадающий с основным URL (например, iataCode уже равен ZZ), бесполезен —
    // без этой проверки imageUrl не изменился бы после ошибки, и повторный эффект загрузки не запустился бы
    const usableFallbackUrl = resolvedFallbackUrl && resolvedFallbackUrl !== primaryUrl ? resolvedFallbackUrl : undefined;
    const imageUrl = primaryFailed && usableFallbackUrl ? usableFallbackUrl : primaryUrl;
    const [svgMarkup, setSvgMarkup] = useState('');
    const isSvg = Boolean(imageUrl && isSvgUrl(imageUrl));
    useEffect(() => {
        setSvgMarkup('');
        if (!isSvg || !imageUrl) {
            return undefined;
        }
        let cancelled = false;
        getSvgContent(imageUrl)
            .then(markup => {
            if (!cancelled) {
                setSvgMarkup(markup);
            }
        })
            .catch(() => {
            if (cancelled) {
                return;
            }
            if (primaryFailedRef.current) {
                setFallbackFailed(true);
            }
            else {
                setPrimaryFailed(true);
            }
            reportError(`CdnImage: не удалось загрузить SVG: ${imageUrl}`);
        });
        return () => {
            cancelled = true;
        };
    }, [imageUrl, isSvg, reportError]);
    useEffect(() => {
        if (!imageUrl || isSvg) {
            return undefined;
        }
        let cancelled = false;
        isImageValid(imageUrl).then(isValid => {
            if (cancelled || isValid) {
                return;
            }
            if (!primaryFailed) {
                setPrimaryFailed(true);
            }
            else {
                setFallbackFailed(true);
            }
            reportError(`CdnImage: не удалось загрузить изображение: ${imageUrl}`);
        });
        return () => {
            cancelled = true;
        };
    }, [imageUrl, isSvg, primaryFailed, reportError]);
    const allFailed = (primaryFailed && !usableFallbackUrl) || fallbackFailed;
    if (allFailed || !imageUrl) {
        return emptyImage !== null && emptyImage !== void 0 ? emptyImage : null;
    }
    if (isSvg) {
        if (isBackground) {
            reportError('CdnImage: isBackground не поддерживается для SVG');
            return emptyImage !== null && emptyImage !== void 0 ? emptyImage : null;
        }
        if (!svgMarkup) {
            return null;
        }
        return (React.createElement(SafeHtml, { sanitizeConfig: { ADD_TAGS: ['use'] }, html: svgMarkup, tag: 'span', className: imgProps.className, style: imgProps.style }));
    }
    if (isBackground) {
        if (isValidElement(children)) {
            return cloneElement(children, {
                style: Object.assign({ backgroundImage: `url(${imageUrl})` }, imgProps.style)
            });
        }
        return (React.createElement("div", { className: imgProps.className, style: Object.assign({ backgroundImage: `url(${imageUrl})` }, imgProps.style) }, children));
    }
    return React.createElement("img", Object.assign({ src: imageUrl, alt: alt }, imgProps));
};
export default CdnImage;
